import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { UserPlus, Building2, Wallet, Users, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'founder'
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error('Please agree to the Privacy Policy and Terms of Use to continue');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, formData);
      login(response.data);
      toast.success('Account created successfully!');
      navigate('/profile-setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'founder', label: 'Founder', icon: Building2, description: 'Seeking capital partnerships' },
    { value: 'investor', label: 'Investor', icon: Wallet, description: 'Discovering aligned ventures' },
    { value: 'cofounder', label: 'Co-founder', icon: Users, description: 'Join a venture' }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F2F6FA] px-4 py-12">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-blue-700 hover:text-blue-500 transition-colors">
        <Home size={18} />
        <span className="text-sm font-medium">Back to Home</span>
      </Link>
      <div className="bg-white border border-slate-200 rounded-lg p-8 w-full max-w-2xl">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="/assets/seedsynch-logo-lockup-dark.svg" 
              alt="SeedSynch - Smarter connections, earlier." 
              className="h-12 sm:h-14 mx-auto"
            />
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="text-blue-800 text-sm font-medium mb-2 block">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="register-name"
                className="input-field"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-blue-800 text-sm font-medium mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="register-email"
                className="input-field"
                placeholder="your@email.com"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-blue-800 text-sm font-medium mb-2 block">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              data-testid="register-password"
              className="input-field"
              placeholder="••••••••"
              required
            />
          </div>

          <div>
            <Label className="text-blue-800 text-sm font-medium mb-4 block">
              I am a...
            </Label>
            <RadioGroup
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value })}
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              {roleOptions.map((option) => (
                <Label
                  key={option.value}
                  htmlFor={option.value}
                  className="cursor-pointer"
                >
                  <div
                    className={`bg-slate-50 border border-slate-300 p-4 rounded-md transition-all duration-300 hover:border-slate-600 ${
                      formData.role === option.value ? 'border-blue-600 bg-blue-600/10' : ''
                    }`}
                    data-testid={`role-${option.value}`}
                  >
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <option.icon size={18} className="text-blue-600" />
                          <span className="font-semibold text-slate-800">{option.label}</span>
                        </div>
                        <p className="text-xs text-slate-600">{option.description}</p>
                      </div>
                    </div>
                  </div>
                </Label>
              ))}
            </RadioGroup>
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="terms"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked)}
              className="mt-1 border-slate-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              data-testid="terms-checkbox"
            />
            <Label htmlFor="terms" className="text-sm text-blue-700 leading-relaxed cursor-pointer">
              By creating an account, you agree to the{' '}
              <Link to="/privacy-policy" className="text-blue-500 hover:text-blue-300 underline" target="_blank">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link to="/terms-of-use" className="text-blue-500 hover:text-blue-300 underline" target="_blank">
                Terms of Use
              </Link>.
              <span className="block text-slate-500 text-xs mt-1">
                Your personal data will be collected and processed to provide matchmaking services.
              </span>
            </Label>
          </div>

          <Button
            type="submit"
            className="btn-primary w-full rounded-md py-6"
            disabled={loading || !agreedToTerms}
            data-testid="register-submit"
          >
            {loading ? 'Creating account...' : (
              <span className="flex items-center justify-center gap-2">
                <UserPlus size={18} />
                Create Account
              </span>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-blue-700 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-500 font-medium" data-testid="login-link">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
